/**
 * Project & section contracts — DESIGN.md §6.1, §6.14, §12.2 (R12, R32).
 */
import { z } from 'zod';
import { PROJECT_HEALTHS, PROJECT_STATUSES } from '../enums.js';
import { colorSchema, name, optionalText, paginationSchema, positionSchema, uuidSchema, } from './common.js';
import { userRefSchema } from './users.js';
/**
 * Project keys appear in URLs (`/projects/TLC`), so they are constrained to
 * something typeable and case-normalised to upper — `tlc` and `TLC` must not
 * be two projects.
 */
export const projectKeySchema = z
    .string()
    .trim()
    .toUpperCase()
    .min(2)
    .max(10)
    .regex(/^[A-Z][A-Z0-9]*$/, 'Use letters and numbers, starting with a letter');
export const sectionSchema = z.object({
    id: uuidSchema,
    projectId: uuidSchema,
    name: z.string(),
    position: z.string(),
    /** Open cards in this section — what the section header shows. */
    taskCount: z.number().int().nonnegative(),
});
export const projectStatusUpdateSchema = z.object({
    id: uuidSchema,
    projectId: uuidSchema,
    health: z.enum(PROJECT_HEALTHS),
    note: z.string(),
    createdBy: userRefSchema,
    createdAt: z.string(),
    /** §6.14 — health older than 14 days is not current information. */
    isStale: z.boolean(),
});
/**
 * Which card fields a project asks for — card #27.
 *
 * Luke's ask: "make a split between what fields are required for every task
 * and optional, and allow per-project config of which to show and ask for in
 * the card (to de-clutter the form)". A support project wants Environment and
 * Testing Outcome on every card; a marketing project wants neither, and every
 * empty row it renders is noise its team scrolls past all day.
 *
 * `title` and `status` are deliberately absent from this vocabulary. §6.2 says
 * a card MUST have both, so they are not configurable — a project cannot hide
 * the two things that make a card a card.
 */
export const CARD_FIELD_KEYS = [
    'description',
    'testingInstructions',
    'assignee',
    'dueDate',
    'startDate',
    'priority',
    'estimate',
    'tags',
    'collaborators',
    'section',
];
/** A custom field's key in this map: `customField:<uuid>` (§6.7). */
export const CUSTOM_FIELD_PREFIX = 'customField:';
/**
 * `hidden` is a *form* instruction and nothing more: the server never drops a
 * value because a project stopped asking for it, and the card panel still
 * shows a hidden field that has a value. Hiding a field must never hide data
 * somebody already entered.
 *
 * `required` is enforced on creation, where the asking happens.
 */
export const cardFieldRuleSchema = z
    .object({
    hidden: z.boolean().optional(),
    required: z.boolean().optional(),
})
    .strict();
export const cardFieldConfigSchema = z
    .record(cardFieldRuleSchema)
    .refine((config) => Object.keys(config).every((key) => CARD_FIELD_KEYS.includes(key) ||
    key.startsWith(CUSTOM_FIELD_PREFIX)), { message: 'Unknown card field' });
/** Whether a project asks for this field at all. Absent means "show it". */
export function isFieldHidden(config, key) {
    return config?.[key]?.hidden === true;
}
/** Whether creating a card in this project requires this field. */
export function isFieldRequired(config, key) {
    return config?.[key]?.required === true;
}
export const projectSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    key: z.string(),
    description: z.string().nullable(),
    color: z.string(),
    icon: z.string().nullable(),
    status: z.enum(PROJECT_STATUSES),
    /**
     * #9 — visible only to the people and groups given access. Carried on the
     * project rather than inferred client-side so the UI can badge it; a viewer
     * who is not allowed to see it never receives the row at all.
     */
    isPrivate: z.boolean(),
    /** Whether *this* viewer may change the privacy setting and the access list. */
    canManageAccess: z.boolean(),
    defaultViewId: uuidSchema.nullable(),
    /** #27 — which card fields this project shows and insists on. */
    cardFields: cardFieldConfigSchema,
    createdBy: userRefSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    archivedAt: z.string().nullable(),
    /** Open, undeleted cards — the number the sidebar and header show. */
    openTaskCount: z.number().int().nonnegative(),
    /** Latest status update, or null when none has been posted (§6.14). */
    latestStatus: projectStatusUpdateSchema.nullable(),
    isFollowing: z.boolean(),
    followerCount: z.number().int().nonnegative(),
});
export const projectWithSectionsSchema = projectSchema.extend({
    sections: z.array(sectionSchema),
});
/** The trimmed shape a card carries for each of its memberships (§6.3). */
export const projectRefSchema = z.object({
    id: uuidSchema,
    key: z.string(),
    name: z.string(),
    color: z.string(),
});
// ---------------------------------------------------------------- requests
export const listProjectsQuerySchema = paginationSchema.extend({
    status: z.enum(['active', 'archived', 'all']).default('active'),
    q: optionalText(200),
});
export const createProjectRequestSchema = z
    .object({
    name: name(200),
    key: projectKeySchema,
    description: optionalText(4000),
    color: colorSchema.default('#6366f1'),
    icon: optionalText(40),
    /** Created alongside the project so a new board is never an empty pane. */
    sectionNames: z.array(name(100)).max(50).default([]),
    /** #9 — private from the first moment, rather than public for a heartbeat. */
    isPrivate: z.boolean().default(false),
})
    .strict();
export const updateProjectRequestSchema = z
    .object({
    name: name(200).optional(),
    key: projectKeySchema.optional(),
    description: optionalText(4000),
    color: colorSchema.optional(),
    icon: optionalText(40),
    defaultViewId: uuidSchema.nullish(),
    isPrivate: z.boolean().optional(),
    /**
     * #27 — replaces the whole map rather than merging keys into it. The
     * editor sends the state of every switch it rendered, so a merge would
     * make "turn this back off" impossible to express.
     */
    cardFields: cardFieldConfigSchema.optional(),
})
    .strict();
// ------------------------------------------------------------------ access
/**
 * Who can see a private project — #9.
 *
 * People and groups are listed together because they answer one question, and
 * a reader deciding whether to add someone needs to see that the group they
 * are in is already there.
 */
export const projectAccessSchema = z.object({
    isPrivate: z.boolean(),
    canManage: z.boolean(),
    /** Always has access and cannot be removed. */
    createdBy: userRefSchema,
    members: z.array(z.object({
        user: userRefSchema,
        addedBy: userRefSchema.nullable(),
        createdAt: z.string(),
    })),
    groups: z.array(z.object({
        id: uuidSchema,
        name: z.string(),
        memberCount: z.number().int().nonnegative(),
        addedBy: userRefSchema.nullable(),
        createdAt: z.string(),
    })),
});
export const updateProjectAccessRequestSchema = z
    .object({
    /** The complete list, replacing what is there — same shape as tags (§6.7). */
    userIds: z.array(uuidSchema).max(500).optional(),
    groupIds: z.array(uuidSchema).max(200).optional(),
})
    .strict();
/** §6.1 — deleting a project requires typing its key back. */
export const deleteProjectRequestSchema = z.object({ confirmKey: z.string() }).strict();
export const createSectionRequestSchema = z
    .object({
    name: name(100),
    /** Omit to append to the end of the project's section list. */
    position: positionSchema.optional(),
})
    .strict();
export const updateSectionRequestSchema = z
    .object({ name: name(100).optional(), position: positionSchema.optional() })
    .strict();
export const createProjectStatusUpdateRequestSchema = z
    .object({
    health: z.enum(PROJECT_HEALTHS),
    note: z.string().trim().min(1).max(5000),
})
    .strict();
//# sourceMappingURL=projects.js.map